import { forwardRef, useId, type InputHTMLAttributes } from 'react';
import clsx from 'clsx';

export interface DateFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string;
  error?: string;
  hint?: string;
}

export const DateField = forwardRef<HTMLInputElement, DateFieldProps>(function DateField(
  { label, error, hint, className, id, ...rest },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? `df-${reactId}`;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;

  return (
    <div className={clsx('relative', className)}>
      <label
        htmlFor={inputId}
        className="block font-mono text-[0.625rem] uppercase tracking-[0.125em] text-ink-subtle mb-1.5"
      >
        {label}
      </label>
      <input
        ref={ref}
        id={inputId}
        type="date"
        aria-invalid={error ? true : undefined}
        aria-describedby={clsx(hintId, errorId) || undefined}
        className={clsx(
          'w-full bg-panel border rounded-md px-3 py-2 min-h-[2.75rem] text-[0.9375rem] font-tnum font-display',
          'transition-colors duration-200',
          'focus:outline-none focus:border-blue focus:ring-2 focus:ring-blue/30',
          error ? 'border-[#b33a3a]' : 'border-hairline hover:border-ink/30',
        )}
        {...rest}
      />
      {hint && !error && (
        <p id={hintId} className="mt-1.5 text-[0.75rem] text-ink-subtle">
          {hint}
        </p>
      )}
      {error && (
        <p
          id={errorId}
          className="mt-1.5 font-mono text-[11px] uppercase tracking-[0.1em] text-[#b33a3a]"
          role="alert"
        >
          &gt; {error}
        </p>
      )}
    </div>
  );
});
